import { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";
import { store } from "../store";
import {
  getWebAuthToken,
  getWebAuthTokenMirror,
} from "../utils/authTokenStorage";

interface OnboardingStatus {
  currentStep: number;
  onboardingCompleted: boolean;
  psaCompleted: boolean;
  documentationCompleted: boolean;
  rmmCompleted: boolean;
  customersCompleted: boolean;
}

export const useOnboardingStatus = (mspId: number | null) => {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = async () => {
    const token =
      (await getWebAuthToken().catch(() => null)) ||
      store?.getState()?.auth?.accessToken ||
      getWebAuthTokenMirror() ||
      "";
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  const fetchStatus = async () => {
    if (!mspId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${config.apiUrl}/onboarding/status`,
        {
          params: { msp_id: mspId },
          headers: await getAuthHeaders(),
        }
      );
      setStatus(response.data);
      setError(null);
    } catch (err: any) {
      console.error("Failed to fetch onboarding status:", err);
      setError(err.message || "Failed to fetch onboarding status");
    } finally {
      setLoading(false);
    }
  };

  const updateProgress = async (step: number, completed: boolean) => {
    if (!mspId) return;

    try {
      await axios.put(
        `${config.apiUrl}/onboarding/progress`,
        {
          msp_id: mspId,
          step,
          completed,
        },
        {
          headers: await getAuthHeaders(),
        }
      );
      await fetchStatus(); // Refresh status after update
    } catch (err: any) {
      console.error("Failed to update onboarding progress:", err);
      throw err;
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [mspId]);

  return {
    status,
    loading,
    error,
    refetch: fetchStatus,
    updateProgress,
  };
};
